import { useMemo, useState } from 'react';
import { BulkAssignListForm, Invoice, MeterPointDetails } from '../../types';
import { FormTabHeadings } from '@portal_components/components/FormTabHeadings';
import { BulkAssignSiteDetailsTab } from './BulkAssignSiteDetailsTab';
import { BulkAssignContactDetailsTab } from './BulkAssignContactDetailsTab';
import { BulkAssignAddressDetailsTab } from './BulkAssignAddressDetailsTab';
import { BulkAssignInvoiceDetailsTab } from './BulkAssignInvoiceDetailsTab';
import { get, useFormContext } from 'react-hook-form';
import { IconButton } from '@portal_components/components/IconButton';
import { FormErrorList } from '@portal_components/components/FormErrorList';

interface BulkAssignRecordFormProps {
  details: Partial<MeterPointDetails>;
  index: number;
  expandedRecordIndex: number | null;
  invoices: Invoice[];
  appointmentActive: boolean;
  appointmentDuplicate?: boolean;
  hasOpenExceptions?: boolean;
  setExpandedRecordIndex: (index: number | null) => void;
  handleRemoveRecord: (index: number, removed_mprn: string) => void;
}

enum EBulkRecordDetailsTab {
  SITE_DETAILS = 'Site Details',
  ADDRESS = 'Address',
  CONTACTS = 'Contact Details',
  INVOICE = 'Invoice'
}

export const BulkAssignRecord = ({
  details,
  index,
  expandedRecordIndex,
  invoices,
  appointmentActive,
  appointmentDuplicate,
  hasOpenExceptions,
  setExpandedRecordIndex,
  handleRemoveRecord
}: BulkAssignRecordFormProps) => {
  const {
    formState: { errors }
  } = useFormContext<BulkAssignListForm>();
  const [currentTab, setCurrentTab] = useState<EBulkRecordDetailsTab>(EBulkRecordDetailsTab.SITE_DETAILS);

  const errorsForRecord = get(errors, `siteList.${index}`);

  const borderColour = useMemo(() => {
    if (!appointmentActive) return 'border-feedback-orange';
    if (errorsForRecord) return 'border-feedback-red';
    return 'border-feedback-blue';
  }, [appointmentActive, errorsForRecord]);

  const handleTabSelected = (tab: EBulkRecordDetailsTab) => () => {
    setCurrentTab(tab);
  };

  const onExpandClicked = () => {
    setExpandedRecordIndex(index !== expandedRecordIndex ? index : null);
  };

  const removeRecord = () => {
    handleRemoveRecord(index, details.mprn ? details.mprn : '');
  };

  return (
    <div key={details.mprn} className="pb-4">
      <div className={`flex flex-col mb-2 gap-1 py-2 px-4 rounded-sm min-h-20 bg-elevation-1 border-solid border-l-[5px] ${borderColour} relative`}>
        <p className="text-lg">MPAN: {details.mprn}</p>
        {!appointmentActive && <p className="text-feedback-orange">No Active Appointment</p>}
        {appointmentDuplicate && <p className="text-feedback-orange">Duplicate MPAN. This MPAN is already assigned to the selected contract</p>}
        {hasOpenExceptions && <p className="text-feedback-orange">This MPAN has an active exception. Resolve the exception or delete it before attempting any other actions for this MPAN</p>}
        <div className="absolute top-5 right-[76px]">
          <IconButton icon="/delete.svg" onClick={removeRecord} />
        </div>
        <div className="absolute top-5 right-4">
          {expandedRecordIndex === index ? <IconButton icon="/arrowUp.svg" onClick={onExpandClicked} /> : <IconButton icon="/arrowDown.svg" onClick={onExpandClicked} />}
        </div>
      </div>
      {expandedRecordIndex === index && details.mprn && (
        <div className="pl-3 pt-2 pb-4 border-b-2 border-b-secondary-deep-blue-80">
          <FormErrorList indexForArray={index} className="text-sm" />
          <FormTabHeadings<EBulkRecordDetailsTab> tabs={Object.values(EBulkRecordDetailsTab)} currentTab={currentTab} handleTabSelected={handleTabSelected} />
          {currentTab === EBulkRecordDetailsTab.SITE_DETAILS && <BulkAssignSiteDetailsTab index={index} />}
          {currentTab === EBulkRecordDetailsTab.CONTACTS && <BulkAssignContactDetailsTab index={index} />}
          {currentTab === EBulkRecordDetailsTab.ADDRESS && <BulkAssignAddressDetailsTab index={index} />}
          {currentTab === EBulkRecordDetailsTab.INVOICE && <BulkAssignInvoiceDetailsTab invoices={invoices} index={index} />}
        </div>
      )}
    </div>
  );
};
